import {ContextMenu, MenuEntryList} from "@/shared/ui/contextMenu";
import {Temporal} from "./utils/datefunctions";

export interface FullCalendarSelection {
	start: Temporal.PlainDate;
	end: Temporal.PlainDate;
}

export class FullCalendarSelectionContextMenu extends ContextMenu<FullCalendarSelection> {
	private static readonly CLOSE_EVENTS = "mousedown.fullCalendarSelectionContextMenu touchstart.fullCalendarSelectionContextMenu";
	private static readonly CONTAINER_CLASS = "full-calendar-selection-context-menu";
	private static readonly MIN_MENU_WIDTH = 240;
	private static readonly VIEWPORT_PADDING = 12;
	private readonly container: JQuery;

	private readonly closeOnOutsideInteraction = (e: JQuery.TriggeredEvent) => {
		// A queued document event can still run after the menu was already removed from the DOM.
		if (!this.container.closest(document.documentElement).length) {
			return;
		}
		if (!$(e.target).closest(this.container).length) {
			this.close();
		}
	};

	constructor(
		private readonly selection: FullCalendarSelection,
		private readonly anchorEvent: MouseEvent,
		menuEntries: MenuEntryList<FullCalendarSelection>,
		private readonly onClose?: () => void
	) {
		super();
		this.container = $("<div>")
			.addClass(FullCalendarSelectionContextMenu.CONTAINER_CLASS)
			.css({
				position: "absolute",
				zIndex: 1060
			});
		this.addMenuEntries(menuEntries);
	}

	public show(): this {
		this.destroy();

		const dropdown = this.renderDropdown(this.selection);
		if (dropdown === "") {
			return this;
		}
		dropdown.css({display: "block", position: "static", float: "none", margin: 0});
		this.container.empty().append(dropdown);
		$(document.body).append(this.container);
		this.positionAtSelection();

		this.container.on("click", "ul.dropdown-menu > li", e => {
			e.preventDefault();
			e.stopPropagation();
			const element = $(e.currentTarget);
			if (!element.hasClass("disabled")) {
				this.triggerAction(element.attr("data-action") ?? "", this.selection, this.container);
				this.close();
			}
		});

		$(document).on(FullCalendarSelectionContextMenu.CLOSE_EVENTS, this.closeOnOutsideInteraction);
		return this;
	}

	public destroy() {
		$(document).off(FullCalendarSelectionContextMenu.CLOSE_EVENTS, this.closeOnOutsideInteraction);
		this.container.off().remove();
	}

	private close(): void {
		this.destroy();
		this.onClose?.();
	}

	private positionAtSelection(): void {
		const menuWidth = Math.max(
			this.container.outerWidth() ?? FullCalendarSelectionContextMenu.MIN_MENU_WIDTH,
			FullCalendarSelectionContextMenu.MIN_MENU_WIDTH
		);
		const menuHeight = this.container.outerHeight() ?? 0;
		const scrollLeft = window.pageXOffset;
		const scrollTop = window.pageYOffset;
		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;
		let left = this.anchorEvent.pageX ?? 0;
		let top = this.anchorEvent.pageY ?? 0;

		// Cap position to the right so it does not overflow the viewport.
		if (left + menuWidth > scrollLeft + viewportWidth - FullCalendarSelectionContextMenu.VIEWPORT_PADDING) {
			left = Math.max(
				scrollLeft + FullCalendarSelectionContextMenu.VIEWPORT_PADDING,
				left - menuWidth
			);
		}

		// Cap position to the bottom so it does not overflow the viewport.
		if (top + menuHeight > scrollTop + viewportHeight - FullCalendarSelectionContextMenu.VIEWPORT_PADDING) {
			top = Math.max(
				scrollTop + FullCalendarSelectionContextMenu.VIEWPORT_PADDING,
				scrollTop + viewportHeight - menuHeight - FullCalendarSelectionContextMenu.VIEWPORT_PADDING
			);
		}

		this.container.css({
			left,
			top,
			width: menuWidth
		});
	}
}
